
import { WizFocusManager } from './../../../../services/core/focus/wiz-focus-manager';
import { BASE_COMPONENT, WizBaseComponent } from './../../../core/wiz-base-component';
import { WizValidatorFn, WizValidators, WizValidationError, WizValidationErrors } from './../../../../services/validators/wiz-validators';
import { DOCUMENT } from '@angular/common';
import { ComponentPortal } from '@angular/cdk/portal';
import { Overlay, OverlayConfig, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { Component, ChangeDetectorRef, ElementRef, Renderer2, ChangeDetectionStrategy, Injector, ComponentRef, Inject, Input,
  ViewContainerRef, forwardRef, AfterViewInit, OnDestroy } from '@angular/core';
import { WizTextInput } from '../../input/wiz-text-input/wiz-text-input';
import { WizTimepicker } from '../wiz-timepicker/wiz-timepicker';
import { IWIZ_COMPONENT} from '../../../../interfaces/iwiz-component';
import { FOCUS_MODE, VALUE_DOWN, VALUE_UP, WIZ_ERROR_MESSAGE, WIZ_EVENT_TYPE, WizDateUtils } from '@wizeoo/akm-core-lib';
import { WizComponentUtils } from '../../../../utils/wiz-component.utils';
import { WizEvent } from '../../../../events/wiz-event';
import { WizEventManager } from '../../../../events/wiz-event.manager';
import { WizComponentTranslationObj } from '../../../../obj/translation/wiz-translation.obj';
import { WizTranslationManager } from '../../../../services/translation/wiz-translation.manager';



/**
 * Input de saisie d'heures
 */
@Component({
  selector: 'wiz-timefield',
  templateUrl: '../../input/wiz-input.html',
  styleUrls: ['../../input/wiz-input.scss', './wiz-timefield.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: BASE_COMPONENT, useExisting: forwardRef(() => WizTimefield) },
    { provide: WizBaseComponent, useExisting: forwardRef(() => WizTimefield) },
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizTimefield) }
  ]
})
export class WizTimefield extends WizTextInput implements AfterViewInit, OnDestroy {

  /** Valeur de l'heur sélectionnée au format HHmm */
  private _wizHourValue: string;
  /** Heure minimale */
  private _minHour: string;
  /** Heure maximale */
  private _maxHour: string;

  /** Flag time picker ouvert */
  private _timePickerOpened: boolean;

  /** Ecart entre chaque minute sélectionnable dans le picker */
  private _minuteStep: number = 1;

  /** Mode affichage avec meridiem */
  private _meridiemMode: boolean;

  /** Saisie manuelle autorisée */
  private _manualInput: boolean = true;

  /** Clé de trad pour le message d'erreur date invalide */
  private _wizInvalidErrorMessage: string;

  /** Flag composant actif (le focus est pris) pour bonne gestion du rollback */
  protected _componentIsActive: boolean = false;

  /** Left icon pour l'ouverture du time picker */
  private readonly _DEFAULT_TIME_PICKER_ICON: string = 'calendar';

  constructor(protected cdref: ChangeDetectorRef, public elementRef: ElementRef, public renderer: Renderer2,
    public overlay: Overlay, private _injector: Injector,
    @Inject(DOCUMENT) protected document: Document) {
    super(cdref, elementRef, renderer);
    this.wizLeftIcon = this._DEFAULT_TIME_PICKER_ICON;
    this.setWizFocusMode(FOCUS_MODE.ROLLBACK);// pas de sortie en erreur sur un timeField (sauf contre-ordre)
  }

  /**
   * @Override
   * Setter de la wizValue avec gestion d'erreur interne
   * @param value Horaire de type string HHmm
   */
  public setWizValue(value: any) {
    if (!this._lastGoodValue && !this._componentIsActive) { // alimentation initiale de la lastGood
      this._lastGoodValue = value;
    }
    this._wizValue = value;
    this._wizHourValue = value;

    if (!this._componentIsActive) {
      // check wizInitialValue pour ne pas afficher d'erreur quand la valeur est alimentée par programmation
      this._hasErrors = false;
    } else if (this.wizMandatory && !value) {// pas de valeur et mandatory
      this._hasErrors = true;
    } else {
      this._hasErrors = false;
    }

    this.manageErrorDisplay();
  }

  /** Getter/setter de la valeur sélectionnée */
  public get wizHourValue(): string {
    return this.getWizHourValue();
  }
  @Input()
  public set wizHourValue(pValue: string) {
    this.setWizHourValue(pValue);
  }
  protected getWizHourValue(): string {
    return this._wizHourValue;
  }
  protected setWizHourValue(pValue: string): void {
    this._wizHourValue = pValue;
    this.wizValue = pValue;
  }

  /** Heure maximale */
  public get wizMaxHour(): string {
    return this.getWizMaxHour();
  }
  @Input()
  public set wizMaxHour(val: string) {
    this.setWizMaxHour(val);
  }
  protected getWizMaxHour(): string {
    return this._maxHour;
  }
  protected setWizMaxHour(val: string): void {
    this._maxHour = val;
  }

  /** Heure minimale */
  public get wizMinHour(): string {
    return this.getWizMinHour();
  }
  @Input()
  public set wizMinHour(val: string) {
    this.setWizMinHour(val);
  }
  protected getWizMinHour(): string {
    return this._minHour;
  }
  protected setWizMinHour(val: string): void {
    this._minHour = val;
  }

  /** Mode saisie manuelle */
  public get wizManualInput(): boolean {
    return this.getWizManualInput();
  }
  @Input()
  public set wizManualInput(val: boolean) {
    this.setWizManualInput(val);
  }
  protected getWizManualInput(): boolean {
    return this._manualInput;;
  }
  protected setWizManualInput(val: boolean): void {
    this._manualInput = val;
    if (val) {
      this._wizInput.nativeElement.removeAttribute('readonly');
    } else {
      this._wizInput.nativeElement.setAttribute('readonly', 'readonly');
    }
    this.checkStyle();
    this.cdref.markForCheck();
  }

  /** Clé de trad pour le message d'erreur horaire invalide */
  public get wizInvalidErrorMessage(): string {
    return this.getWizInvalidErrorMessage();
  }
  @Input()
  public set wizInvalidErrorMessage(val: string) {
    this.setWizInvalidErrorMessage(val);
  }
  protected getWizInvalidErrorMessage(): string {
    return this._wizInvalidErrorMessage;
  }
  protected setWizInvalidErrorMessage(val: string): void {
    this._wizInvalidErrorMessage = val;
    this.cdref.markForCheck();
  }

  /** Getter/setter du pas de minutes */
  public get wizMinuteStep(): number {
    return this.getWizMinuteStep();
  }
  @Input()
  public set wizMinuteStep(value: number) {
    this.setWizMinuteStep(value);
  }
  public getWizMinuteStep(): number {
    return this._minuteStep;
  }
  public setWizMinuteStep(value: number) {
    if (value && value > 0) {
      this._minuteStep = value;
    }
  }

  /** Valeur de l'input interne */
  public getInnerInputValue(): string {
    const localeMask: string = WizTranslationManager.getInstance().translateKey('hour.format', null, 'HH:mm');
    const value: string = WizDateUtils.dateToString(WizDateUtils.hourToDate(this._wizHourValue), localeMask);
    return value || this._wizHourValue || '' ; // ? value : '';
  }

  /**
   * Override: pas de changement d'input type
   */
  public setWizInputType(pValue: string) {
    return;
  }

  /** accesseur sur la valeur actuelle du composant de saisie */
  // private get wizInputValue(): string {
  //   return this._wizInput ? this._wizInput.nativeElement.value : null;
  // }

  /** Style du pointeur de souris sur l'input, selon édition / disabled */
  public getInputPointer(): string {
    return this.wizManualInput || this.wizDisabled ? '' : 'pointer';
  }

  public ngAfterViewInit() {
    // key down pour les touches hors saisie > fermeture/ouverture avec les flèches
    WizEventManager.addListener(this, this._wizInput, 'keydown', (e: KeyboardEvent) => this.onKeyDown(e));
    // key press pour restriction de saisie
    WizEventManager.addListener(this, this._wizInput, 'keypress', (e: KeyboardEvent) => this.onKeyPress(e));
    // ecoute de l'evenement paste pour empécher de coller des caractères non valides
    WizEventManager.addListener(this, this._wizInput, 'paste', (e: ClipboardEvent) => this.onPaste(e));
  }

  /**
   * Suppression des écouteurs
   */
  ngOnDestroy() { // liaison du dateFilter du picker à la filterFunc
    super.ngOnDestroy();
    WizEventManager.removeListener(this, this._wizInput, 'keydown');
    WizEventManager.removeListener(this, this._wizInput, 'keypress');
    WizEventManager.removeListener(this, this._wizInput, 'paste');
  }

  /**
   * Ouverture du time picker au click sur le left icon
   */
  public wizOnLeftIconClicked(): void {
    if (!this.getWizDisabled()) {
      // suppression du message d'error à l'ouverture du calendar
      if (this._hasErrors) {
        this._hasErrors = false;
        this.manageErrorDisplay();
      }
      const overlayRef: OverlayRef = this.overlay.create(this.getOverlayConfig());
      overlayRef.overlayElement.setAttribute('role', 'dialog');
      const injector = Injector.create({
        providers: [{ provide: ChangeDetectorRef, useValue: this.cdref },
        { provide: Document, useValue: this.document },
        { provide: OverlayRef, useValue: overlayRef }],
        parent: this._injector
      });
      const timePickerPortal = new ComponentPortal(WizTimepicker, null, injector);

      // Attach ComponentPortal to PortalHost
      const timePickerRef: ComponentRef<WizTimepicker> = overlayRef.attach(timePickerPortal);
      // Passage des données utiles au time picker
      timePickerRef.instance.wizValidHourCallBack = this.onTimePickerValid.bind(this);
      timePickerRef.instance.wizMeridiemMode = this.getMeridiemMode();
      timePickerRef.instance.wizMinuteStep = this._minuteStep;
      timePickerRef.instance.wizMinHour = this.wizMinHour;
      timePickerRef.instance.wizMaxHour = this.wizMaxHour;
      timePickerRef.instance.wizHourValue = this._wizHourValue;
      this._timePickerOpened = true;
      // Déclaration de filiation au focusmanager
      WizFocusManager.getInstance().wizRegisterOwner(this, timePickerRef.instance.elementRef);
      // Subscribe to a stream that emits when the backdrop was clicked
      overlayRef.backdropClick().subscribe(_ => overlayRef.dispose());
      overlayRef.detachments().subscribe(_ => this.onTimePickerClose());
      // Focus au time picker
      timePickerRef.instance.wizFocus();
    }
  }

  /**
   * Callback de sélection d'heure par le time picker
   * @param pSelectedHour Herue sélectionnée au format HHmm
   */
  private onTimePickerValid(pSelectedHour: string): void {
    if (!this.wizManualInput && this.getWizHourValue() !== pSelectedHour) { // changement et pas de saisie manuel > evenement change
      WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this.wizValue));
    }
    else if (this.wizManualInput && this.getWizHourValue() === pSelectedHour) {
      this._wizInput.nativeElement.value = this.getInnerInputValue();
    }
    this.setWizHourValue(pSelectedHour);
  }

  /**
   * reprise du focus à la fermeture du time picker
   */
  private onTimePickerClose(): void {
    this._timePickerOpened = false;
    this._wizInput.nativeElement.focus();
    this.wizGetFocus();
  }

  /**
   * Configuration de l'overlay du time picker
   */
  private getOverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'wiz-popup-overlay',
      panelClass: ['wiz-popup-panel','wiz-popup-panel-timepicker', 'ag-custom-component-popup'],
      // l'overlay va prendre sa position en fonction du wiz-component-field
      positionStrategy: this.getOverlayPosition(this._wizInput.nativeElement.parentNode),
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    });
  }

 /**
  * Configuration de la position de l'overlay du time picker
  * @param origin Point d'ancrage de l'overlay
  */
  private getOverlayPosition(origin: ElementRef): PositionStrategy {
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(origin)
      .withPositions(WizComponentUtils.getPreferredPositions())
      .withPush(false);
    return positionStrategy;
  }

  /* ############# Events ################ */

  /**
   * Handler de clic dans l'input, simuler le clic sur time picker
   * @param event Evenement clic
   */
  public inputClic(event: MouseEvent) {
    if (!this.wizManualInput) {
      event.preventDefault();
      this.wizOnLeftIconClicked();
    }
  }

  /**
   * Ecouteur sur key down pour ouverture du panneau time picker
   * @param event Evènement key down
   */
  private onKeyDown(event: KeyboardEvent): void {
    // ouverture sur up / down / space
    if (event.key === VALUE_DOWN || event.key === VALUE_UP) {
      if (!this._timePickerOpened) {
        this.wizOnLeftIconClicked();
      }
    }
  }

  /**
   * Restriction de saisie dans l'input
   * @param event Evenement clavier
   */
  public onKeyPress(event: KeyboardEvent) {
    if (this._hasErrors) { // suppression éventuelle de l'affichage d'erreur
      this._hasErrors = false;
      this.manageErrorDisplay();
    }
    // Les caractères spéciaux ne génèrent pas de keypress
    this.preventInsert(event.key, event);
  }

  /**
   * appelé lors d'un coller dans l'input
   * cette méthode récupère le texte à coller et appelle la méthode wizPreventInsert
   * On écoute l'évenement paste en plus du keydown ctrl + v || shift + Inser, car un coller peut être déclenché par la souris
   * @param event Paste event
   */
   public onPaste(event: ClipboardEvent): void {
    // le caractère en cours
    const inputChar = event.clipboardData.getData('Text');
    this.preventInsert(inputChar, event);
  }

  /**
   * Annule l'insertion d'un caractère ou chaine de caractère
   * Si cette valeur est invalide par rapport à dateRegExp
   * @params value, le caratère ou la chaine à inserer
   * @params event, l'event à prevent si value n'est pas autorisé
   */
   public preventInsert(value: string, event: Event): void {
    // La chaine existante
    const currentString: string = this.wizInputValue;
    // la future chaine
    const selectionStart: number = this._wizInput.nativeElement.selectionStart;
    const selectionEnd: number = this._wizInput.nativeElement.selectionEnd;
    const next: string = currentString.substr(0, selectionStart) + value + currentString.substr(selectionEnd);

    // controle sur base de la regExp
    if (next && !String(next).match(this.hourRegExp)) {
      event.preventDefault();
    }
  }

  /** RegExp pour contrôle de saisie */
  private get hourRegExp(): RegExp {
    // TODO prise en compte séparateur
    const meridiemRegexp: string = this.getMeridiemMode() ? '((\ {0,1})|(\ (a|A|p|P){0,1})|(\ (a|A|p|P)(m|M){0,1}))' : '';
    const regexStr: string = '(^\\d{1,2}\:{0,1}$)|(^\\d{1,2}\:{0,1}\\d{1,2}' + meridiemRegexp + '$)';
    return new RegExp(regexStr, 'g');
  }

  /**
   * @Override
   * Surcharge pour ne pas réagir au change.
   * De cette façon, l'input n'envoie pas d'évènements WIZ_CHANGE à la saisie.
   * La validation de données se fait au blur si saisie manuelle possible ou au retour de la pop timepicker si pas de saisie manuelle
   */
  public onChange(e: string): void {
    return;
  }

  /** sur prise de focus, mémorisation de la valeur pour dispatch si changement en sortie */
  public onFocus(e: Event): void {
    if (!this._wizInitialValue) {// annulée sur focusOut véritable
      this._componentIsActive = true;
      this._wizInitialValue = this.getWizValue();
    }

    if (e) {// c'est une prise de focus, pas un clic sur clear
      this.wizGetFocus();
    }
  }


  /** sur perte de focus, gestion de l'interne (pop) et de la validation */
  public onBlur(e: Event): void {
    // fausse sortie, je reviens de la popUp
    if (WizFocusManager.getInstance().isCurrentFocused(e.target)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    } else {
      // supprimer l'indicateur d'ouverture de pop
      this._timePickerOpened = false;

      // Validation en mode saisie manuelle en sortie de composant
      if (this.wizManualInput) {
        // changement
        if (this.getWizValue() !== this._wizInitialValue) {
          WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this.wizValue));
        } else {
          this._wizInput.nativeElement.value = this.getInnerInputValue();
        }

      }
      this._wizInitialValue = null;
      this._componentIsActive = false;
      this.wizLoseFocus();
    }
  }

  // sur wizValidation, alimentation du wizValue avec le prototype en saisie
  public prepareForCheck(): void {
    this.setWizHourValue(this.getHourFromInner());
  }

  /**
   * construction d'une heure depuis la saisie en cours
   * utilisé avant focusOut pour setter WizValue (pour cycle de validation)
   */
  private getHourFromInner(): string {
    const hourInput: string = this.wizInputValue;

    // on essaye de transformer en horaire valide
    const hour: string = WizDateUtils.parseHour(hourInput);
    return hour;
  }

  /**
   * Override du clear wizHourValue
   * @param: e Event. Ne sert actuellement pas mais utile pour un traitement spécifique sur la target
   */
  public onClear(e: Event) {
    if (!this.getWizDisabled()) {
      this.setWizHourValue(null);
      WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this._wizValue));
      // Donne le focus à l'input
      this.wizFocus();
    }
  }

  /* ################### VALIDATION ####################### */

  /**
   * Appel externe sur focusOut depuis la directive wizValidator
   * la mise à jour des données est réalisée par dateCheck
   * @param pErrors la collection d'erreur
   * @param wizShowErrors
   */
  public onWizValidation(pErrors: WizValidationErrors, wizShowErrors: boolean): void {
    this._hasErrors = pErrors.length > 0;
    if (!this._hasErrors) {
      this._lastGoodValue = this.wizHourValue;
    }
    this.manageErrorDisplay();
  }

  /** mode focus.rollback, retour dernière bonne config */
  public wizRollback(): void {
    setTimeout(() => { // sinon trop tôt, pas modifié
      this._wizHourValue = this._lastGoodValue;
      this._wizInput.nativeElement.value = this.getInnerInputValue();
    }, 0);

  }

  /** suppression / affichage des marqueurs d'erreur & messages */
  protected manageErrorDisplay(): void {
    if (this._hasErrors) {
      // TODO marche pas 1er focus Out
    }
    else {
      if (this.getErrorVCRef()) {// clear container
        this.getErrorVCRef().clear();
      }
    }
    this.cdref.markForCheck();
  }

  /** Error message container : contrat d'interface pour la directive wizValidator */
  public getErrorVCRef(): ViewContainerRef {
    return this.errorContainer;
  }

  /**
   * initialisation des méthodes de validation
   * @return WizValidatorFn[] Tableau des methodes de validation
   */
  public getValidators(): WizValidatorFn[] {
    const validators: WizValidatorFn[] = [];

    let invalidHourMessage = '';
    // if wizMandatory is true, the required validator in added to wizValidatorsArray
    if (this.wizMandatory) {
      const errorMessage = this.wizRequiredErrorMessage ? this.wizRequiredErrorMessage : WIZ_ERROR_MESSAGE.REQUIRED;
      validators.push(WizValidators.wizRequired(errorMessage));
    }

    invalidHourMessage = this.wizInvalidErrorMessage ? this.wizInvalidErrorMessage : WIZ_ERROR_MESSAGE.INVALID_HOUR;
    // vrai validateur du TimeField. Appel à hourCheck
    validators.push(this.wizValidHour(invalidHourMessage));

    return validators;
  }

  /**
   * Méthode de validation de la date saisie (check du WizErrorManager)
   * @param errorMessage, Le message d'erreur
   * @return WizValidatorFn
   */
  private wizValidHour(errorMessage: string): WizValidatorFn {
    return (compo: WizTimefield): WizValidationErrors | null => {
      const temp: WizValidationError = {
        wizError: errorMessage
      };
      return (this.hourCheck()) ? null : [temp];
    };
  }

  /**
   * Test de la validité d'une date
   * - Définie si mandatory
   * - entre wizMinHour et wizMaxHour si définis
   * @return boolean Le résultat du test
   */
  private hourCheck(): boolean {

    const hour: string = this.getHourFromInner();
    if (!hour && this.wizInputValue) { // erreur de format
      return false;
    }

    if (this.wizMandatory && !hour) { // pas de date sur mandatory
      return false;
    }

    if (this.wizMinHour && WizDateUtils.isHourBefore(hour, this.wizMinHour)) { // trop tôt
      return false;
    }

    if (this.wizMaxHour && WizDateUtils.isHourAfter(hour, this.wizMaxHour)) { // trop tard
      return false;
    }

    return true;
  }

  /**
   * @override
   * Traduction
   * @param compTrsVOList component translations array
   */
  wizTranslate(compTrsVOList: WizComponentTranslationObj[]): void {
    this._meridiemMode = this.isMeridiemMode();
    super.wizTranslate(compTrsVOList);
  }

  /**
   * Acces au mode meridiem avec initialisation si nécessaire
   */
  protected getMeridiemMode(): boolean {
    if (!this._meridiemMode) {
      this._meridiemMode = this.isMeridiemMode();
    }
    return this._meridiemMode;
  }

  /**
   * Mode affichage avec meridiem
   * @return boolean
   */
  private isMeridiemMode(): boolean {
    const localeMask: string = WizTranslationManager.getInstance().translateKey('hour.format', null, 'HH:mm');
    return localeMask.indexOf('a') >= 0;
  }

  /* ########## STYLE ############# */

  /**
   * @override
   * Application du style
   */
  checkStyle() {
    super.checkStyle();
    this._wizInput.nativeElement.style.cursor = this.wizManualInput || this.wizDisabled ? '' : 'pointer';
  }

  /** Extra CSS class pour le date picker */
  private _wizExtraClassDatePicker: string = '';
  @Input()
  public set wizExtraClassDatePicker(cssClass: string) {
    this.setWizExtraClassDatePicker(cssClass);
  }
  public get wizExtraClassDatePicker(): string {
    return this.getWizExtraClassDatePicker();
  }
  public setWizExtraClassDatePicker(cssClass: string): void {
    this._wizExtraClassDatePicker = cssClass;
  }
  public getWizExtraClassDatePicker(): string {
    return this._wizExtraClassDatePicker;
  }


}
